import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import { IsBoolean, IsNumber, IsOptional, IsString, ValidateNested } from "class-validator";
import { Subject, Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class FloorplanTransmitter extends FloorplanProductItem<TransmitterConfiguration> {
  @Exclude()
  DEFAULT_MONITORING_AREA_RADIUS: number = 2.5;

  @Type(() => TransmitterConfiguration)
  @ValidateNested()
  @Exclude()
  private _transmitterConfiguration: TransmitterConfiguration;

  @IsString()
  @Expose({ name: "transmitterId" })
  private readonly _transmitterId: string;

  @IsNumber()
  @Expose({ name: "monitoringAreaRadius" })
  @Transform((params) => params.value || 375, { toClassOnly: true })
  private _monitoringAreaRadius: number = 375;

  @IsString()
  @IsOptional()
  @Expose({ name: "bmk" })
  private _bmk?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: "sector" })
  private _sector?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: "installationHeight" })
  private _installationHeight?: string;

  @IsBoolean()
  @IsOptional()
  @Expose({ name: "exProtection" })
  private _exProtection?: boolean;

  @IsInstanceOf(FloorplanRemoteSensor)
  @Type(() => FloorplanRemoteSensor)
  @IsOptional()
  @Expose({ name: "remoteSensor" })
  private _remoteSensor?: FloorplanRemoteSensor;

  @Exclude()
  private referenceMeasureSub?: Subscription;

  @IsNumber()
  private _referenceMonitoringAreaRadius: number = this.DEFAULT_MONITORING_AREA_RADIUS;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceMonitoringAreaRadiusOverride" })
  private _referenceMonitoringAreaRadiusOverride?: number;

  @Exclude()
  public monitoringAreaRadiusChanged: Subject<{ radius: number }>;

  @Exclude()
  referenceMonitoringAreaRadiusChanged: Subject<{ referenceLength: number }>;

  constructor(
    floorplan: Floorplan,
    transmitter: TransmitterConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    monitoringAreaRadius: number = 375,
    remoteSensor?: FloorplanRemoteSensor,
    bmk?: string,
    sector?: string,
    installationHeight?: string,
    exProtection?: boolean,
    notes?: string,
  ) {
    super(floorplan, transmitter, id, x, y, size, notes);
    this._transmitterId = transmitter?.id;
    this._transmitterConfiguration = transmitter;
    this._monitoringAreaRadius = monitoringAreaRadius;
    this._referenceMonitoringAreaRadius = transmitter?.defaultMonitoringAreaRadius ?? this.DEFAULT_MONITORING_AREA_RADIUS;
    this._remoteSensor = remoteSensor;
    this._bmk = bmk;
    this._sector = sector;
    this._installationHeight = installationHeight;
    this._exProtection = exProtection;
    this.monitoringAreaRadiusChanged = new Subject();
    this.referenceMonitoringAreaRadiusChanged = new Subject();
    this.updateMonitoringAreaRadiusByReferenceMeasure();
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.transmittersItemTextVisible;
  }

  override init(floorplan: Floorplan) {
    super.init(floorplan);
    if (!this.config) {
      return;
    }
    this.applyInitialDetectionRange();
    if (this._remoteSensor) {
      this._remoteSensor.init(floorplan, this);
    }
  }

  protected override getConfigFromProject(floorplan: Floorplan): TransmitterConfiguration | undefined {
    return floorplan.getTransmitterConfig(this._transmitterId);
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const config = this.config;
    const bmk = this.bmk;
    const exProtection = this.exProtection;
    const installationHeight = this.installationHeight;
    const monitoringAreaRadius = this.monitoringAreaRadius;
    const sector = this.sector;
    const size = this.size;

    return (x: number, y: number) =>
      FloorplanTransmitter.create(
        this._floorplan,
        config,
        x,
        y,
        size,
        monitoringAreaRadius,
        undefined,
        bmk,
        sector,
        installationHeight,
        exProtection,
      );
  }

  protected override afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    const afterDuplicateAdded = super.afterDuplicateAdded();
    const referenceMonitoringAreaRadius = this.isDetectionRangeOverridden ? this.referenceMonitoringAreaRadius : undefined;
    const hasRemoteSensor = !!this.remoteSensor;

    return (duplicate: FloorplanItem) => {
      const duplicateTransmitter = duplicate as FloorplanTransmitter;
      afterDuplicateAdded?.(duplicateTransmitter);

      if (referenceMonitoringAreaRadius !== undefined) {
        duplicateTransmitter.updateMonitoringAreaRadius(referenceMonitoringAreaRadius);
      }

      if (hasRemoteSensor) {
        duplicateTransmitter.attachRemoteSensor();
      }
    };
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.transmitters;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanTransmitter,
        this._floorplan.transmitters,
        FloorplanEventType.TRANSMITTER_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanTransmitter) => void) | undefined,
      );
  }

  override delete() {
    this._floorplan.deleteTransmitter(this);
  }

  get monitoringAreaRadius(): number {
    return this._monitoringAreaRadius;
  }

  set monitoringAreaRadius(value: number) {
    if (this._floorplan.referenceMeasure) {
      value = this.calculateMonitoringArea();
    }
    this._monitoringAreaRadius = value;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  get referenceMonitoringAreaRadius() {
    return this._referenceMonitoringAreaRadiusOverride ?? this._referenceMonitoringAreaRadius;
  }

  get isDetectionRangeOverridden(): boolean {
    return this._referenceMonitoringAreaRadiusOverride !== undefined;
  }

  get bmk(): string | undefined {
    return this._bmk;
  }

  set bmk(value: string) {
    this._bmk = value;
  }

  get sector(): string | undefined {
    return this._sector;
  }

  set sector(value: string) {
    this._sector = value;
  }

  get installationHeight(): string | undefined {
    return this._installationHeight;
  }

  set installationHeight(value: string) {
    this._installationHeight = value;
  }

  get exProtection(): boolean | undefined {
    return this._exProtection;
  }

  set exProtection(value: boolean) {
    this._exProtection = value;
  }

  get remoteSensor(): FloorplanRemoteSensor | undefined {
    return this._remoteSensor;
  }

  public isMonitoringAreaScaleAble(): boolean {
    return !this._floorplan.referenceMeasure;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  updateMonitoringAreaRadius(radius: number) {
    this._referenceMonitoringAreaRadiusOverride = radius;
    this._referenceMonitoringAreaRadius = radius;
    if (this.isReferenceMeasureSet()) {
      this._monitoringAreaRadius = radius * this._floorplan.getReferenceScale();
    }
    this._floorplan.publishUpdate(FloorplanEventType.MONITORING_AREA_RADIUS_UPDATED, this);
    this.monitoringAreaRadiusChanged.next({ radius: this.referenceMonitoringAreaRadius });
  }

  resetDetectionRangeToDefault() {
    this._referenceMonitoringAreaRadiusOverride = undefined;
    this._referenceMonitoringAreaRadius = this.config.defaultMonitoringAreaRadius;
    this.updateMonitoringAreaRadiusByReferenceMeasure();
  }

  setDetectionRangeOverrideFromCurrent() {
    this.updateMonitoringAreaRadius(this.referenceMonitoringAreaRadius);
  }

  applyDefaultDetectionRange() {
    if (this._referenceMonitoringAreaRadiusOverride !== undefined) {
      return;
    }
    this._referenceMonitoringAreaRadius = this.config.defaultMonitoringAreaRadius;
    this.updateMonitoringAreaRadiusByReferenceMeasure();
  }

  updateMonitoringAreaRadiusByReferenceMeasure(publishUpdate = true): void {
    if (this.isReferenceMeasureSet()) {
      this._monitoringAreaRadius = this.calculateMonitoringAreaByReference();
      if (publishUpdate) {
        this._floorplan.publishUpdate(FloorplanEventType.MONITORING_AREA_RADIUS_UPDATED, this);
      }
      this.monitoringAreaRadiusChanged.next({ radius: this.referenceMonitoringAreaRadius });
    }
  }

  attachRemoteSensor() {
    this._remoteSensor = new FloorplanRemoteSensor(
      this._floorplan,
      this,
      uuidv4(),
      this.x,
      this.y + this.monitoringAreaRadius,
      this.size,
    );
    this._floorplan.publishUpdate(FloorplanEventType.REMOTE_SENSOR_ATTACHED, this);
  }

  detachRemoteSensor() {
    this._remoteSensor = undefined;
    this._floorplan.publishUpdate(FloorplanEventType.REMOTE_SENSOR_DETACHED, this);
  }

  static create(
    floorplan: Floorplan,
    transmitter: TransmitterConfiguration,
    x: number,
    y: number,
    size: number,
    monitoringAreaRadius: number,
    remoteSensor?: FloorplanRemoteSensor,
    bmk?: string,
    sector?: string,
    installationHeight?: string,
    exProtection?: boolean,
  ): FloorplanTransmitter {
    return new FloorplanTransmitter(
      floorplan,
      transmitter,
      uuidv4(),
      x,
      y,
      size,
      monitoringAreaRadius,
      remoteSensor,
      bmk,
      sector,
      installationHeight,
      exProtection,
    );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshTransmittersLock();
  }

  private calculateMonitoringAreaByReference(): number {
    return Math.round(this.referenceMonitoringAreaRadius * this._floorplan.getReferenceScale() * 100) / 100;
  }

  private calculateMonitoringArea(): number {
    return Math.round((this.monitoringAreaRadius / this._floorplan.getReferenceScale()) * 100) / 100;
  }

  private applyInitialDetectionRange() {
    const defaultRadius = this.config.defaultMonitoringAreaRadius;

    if (this._referenceMonitoringAreaRadiusOverride !== undefined) {
      this.updateMonitoringAreaRadiusByReferenceMeasure(false);
      return;
    }

    if (!this._floorplan.referenceMeasure) {
      this._referenceMonitoringAreaRadius = defaultRadius;
      return;
    }

    const inferredReferenceRadius = this.inferReferenceRadiusFromMonitoringArea();

    if (inferredReferenceRadius !== undefined && this.shouldOverrideReferenceRadius(inferredReferenceRadius, defaultRadius)) {
      this._referenceMonitoringAreaRadiusOverride = inferredReferenceRadius;
      this._referenceMonitoringAreaRadius = inferredReferenceRadius;
    } else {
      this._referenceMonitoringAreaRadius = defaultRadius;
    }

    this.updateMonitoringAreaRadiusByReferenceMeasure(false);
  }

  private inferReferenceRadiusFromMonitoringArea(): number | undefined {
    const referenceScale = this._floorplan.getReferenceScale();
    return referenceScale ? this._monitoringAreaRadius / referenceScale : undefined;
  }

  private shouldOverrideReferenceRadius(inferredReferenceRadius: number | undefined, defaultRadius: number): boolean {
    if (inferredReferenceRadius === undefined) {
      return false;
    }
    const difference = Math.abs(inferredReferenceRadius - defaultRadius);
    return difference > 0.001;
  }
}
